import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import logger from '../utils/logger';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Union type of all available dev flag keys.
 * Add new keys here when adding new dev flags.
 */
export type DevFlagKey = 'debugUI' | 'mouseZoom';

/**
 * Static metadata defining each available dev flag.
 */
export interface DevFlagDefinition {
  /** Unique identifier for the flag */
  key: DevFlagKey;
  /** Human-readable label shown in UI */
  label: string;
  /** Emoji icon for the flag */
  emoji: string;
  /** Value when not set or on fresh install */
  defaultValue: boolean;
  /** Tooltip/help text explaining the flag */
  description?: string;
}

/**
 * Runtime state containing current values of all flags.
 */
export type DevFlagsState = Record<DevFlagKey, boolean>;

/**
 * Context value exposed by the DevFlagsProvider.
 */
export interface DevFlagsContextType {
  /** Current values of all flags */
  flags: DevFlagsState;
  /** Toggle a flag on/off */
  toggleFlag: (key: DevFlagKey) => void;
  /** Set flag to specific value */
  setFlag: (key: DevFlagKey, value: boolean) => void;
  /** Get current flag value */
  getFlag: (key: DevFlagKey) => boolean;
}

// ============================================================================
// Flag Registry
// ============================================================================

/**
 * Central registry of all available dev flags.
 * Add new flags here - they automatically appear in the Dev Flags menu.
 */
export const DEV_FLAGS: readonly DevFlagDefinition[] = [
  {
    key: 'debugUI',
    label: 'Debug UI',
    emoji: '🐛',
    defaultValue: false,
    description: 'Show debug overlays on photo components',
  },
  {
    key: 'mouseZoom',
    label: 'Mouse Zoom',
    emoji: '🔍',
    defaultValue: false,
    description: 'Enable mouse wheel and pinch-to-zoom in critique mode',
  },
] as const;

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY = '206mp_dev_flags';

/**
 * Build default state from flag definitions.
 */
const getDefaultFlags = (): DevFlagsState => {
  return DEV_FLAGS.reduce((acc, flag) => {
    acc[flag.key] = flag.defaultValue;
    return acc;
  }, {} as DevFlagsState);
};

/**
 * Load flags from localStorage, merging with defaults for forward compatibility.
 */
const loadFlags = (): DevFlagsState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new flags added after storage was created
      return { ...getDefaultFlags(), ...parsed };
    }
  } catch (error) {
    logger.warn('DevFlags', 'Failed to load flags from localStorage:', error);
  }
  return getDefaultFlags();
};

// ============================================================================
// Context
// ============================================================================

const DevFlagsContext = createContext<DevFlagsContextType | undefined>(
  undefined
);

/**
 * Props for DevFlagsProvider component.
 */
export interface DevFlagsProviderProps {
  children: ReactNode;
}

/**
 * Provider component for global dev flags state.
 * Manages all dev flags with localStorage persistence.
 */
export function DevFlagsProvider({ children }: DevFlagsProviderProps) {
  const [flags, setFlags] = useState<DevFlagsState>(loadFlags);

  // Persist to localStorage whenever flags change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    } catch (error) {
      logger.warn('DevFlags', 'Failed to persist flags to localStorage:', error);
    }
  }, [flags]);

  const toggleFlag = useCallback((key: DevFlagKey) => {
    setFlags((prev) => {
      const newValue = !prev[key];
      logger.info('DevFlags', `Flag toggled: ${key} = ${newValue}`);
      return { ...prev, [key]: newValue };
    });
  }, []);

  const setFlag = useCallback((key: DevFlagKey, value: boolean) => {
    setFlags((prev) => {
      if (prev[key] === value) return prev;
      logger.info('DevFlags', `Flag set: ${key} = ${value}`);
      return { ...prev, [key]: value };
    });
  }, []);

  const getFlag = useCallback(
    (key: DevFlagKey): boolean => {
      return flags[key];
    },
    [flags]
  );

  const value: DevFlagsContextType = {
    flags,
    toggleFlag,
    setFlag,
    getFlag,
  };

  return (
    <DevFlagsContext.Provider value={value}>
      {children}
    </DevFlagsContext.Provider>
  );
}

/**
 * Hook to access dev flags state and controls.
 * Can be used with or without a DevFlagsProvider.
 *
 * @returns Dev flags state and control functions, or undefined if no provider
 */
export function useDevFlags(): DevFlagsContextType | undefined {
  const context = useContext(DevFlagsContext);
  return context;
}
